import { AuthProvider, useAuth } from 'react-oidc-context';
import React, { useEffect, useRef } from 'react';

interface IAuth {
  children: React.ReactElement;
}

const getSafeRedirectPath = (state?: string) => {
  if (!state) return '/';

  try {
    const parsed = new URL(state, window.location.origin);

    if (parsed.origin !== window.location.origin) {
      return '/';
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
  } catch {
    return '/';
  }
};

const oidcConfig = {
  authority: process.env.REACT_APP_OIDC_ISSUER as string,
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID as string,
  redirect_uri: process.env.REACT_APP_BASE_URL as string,
};

const AuthComponent = (props: IAuth) => {
  return (
    <AuthProvider
      {...oidcConfig}
      onSigninCallback={(User) => {
        const redirectURI = getSafeRedirectPath(
          User?.state as string | undefined
        );
        window.history.replaceState({}, document.title, redirectURI);
      }}
    >
      <OIDCProvider>{props.children}</OIDCProvider>
    </AuthProvider>
  );
};

const OIDCProvider = ({ children }: IAuth) => {
  const auth = useAuth();
  const isRedirecting = useRef(false);

  const login = async () => {
    const { href } = window.location;

    if (auth.isLoading || auth.isAuthenticated || isRedirecting.current) {
      return;
    }

    try {
      isRedirecting.current = true;
      await auth.signinRedirect({ state: href });
    } catch (error) {
      isRedirecting.current = false;
      throw error;
    }
  };

  const relogin = async () => {
    const { href } = window.location;

    if (
      auth.isLoading ||
      !auth.isAuthenticated ||
      auth.user?.expired !== true ||
      isRedirecting.current
    ) {
      return;
    }

    try {
      isRedirecting.current = true;
      await auth.signinRedirect({ state: href });
    } catch (error) {
      isRedirecting.current = false;
      throw error;
    }
  };

  useEffect(() => {
    login();
  }, [auth.isLoading, auth.isAuthenticated]);

  useEffect(() => {
    relogin();
  }, [auth.isLoading, auth.isAuthenticated, auth.user?.expired]);

  useEffect(() => {
    if (auth.user?.access_token) {
      sessionStorage.setItem('token', auth.user.access_token);
      isRedirecting.current = false;
      return;
    }

    sessionStorage.removeItem('token');
  }, [auth.user?.access_token]);

  if (auth.isLoading) return <></>;

  if (!auth.isAuthenticated || !auth.user?.access_token) return <></>;

  return children;
};

export default AuthComponent;
